import Dispatcher from "../dispatcher";
import BookHelpers from "./book-helpers";
import HighlightStore from "./highlight-store";
import BookmarkStore from "./bookmark-store";
import BookUtils from "../utils/book-utils";
import BookAction from "../actions/book-action";

/*
  Very custom store for Jigsaw book loading
  Available Functions:
    - get(isbn) : returns the book object which may or may not be loaded
    - getHighlights(isbn)
    - getPageBreaks(isbn)
    - getTOC(isbn)
    -
*/
const BookStore = {
  _state: {},

  isLoading: false,

  // Get specific book by isbn
  get(isbn) {
    let book = this._state[isbn];
    // init a book
    book = this._state[isbn] = book || this._getBlank(isbn);
    return book;
  },

  _getBlank(isbn) {
    let store = this;
    return {
      isbn: isbn,
      _updateState(state) {
        store._updateState(this.isbn, state);
      },
      _destroy() {
        BookHelpers.unbindJigsawEvents(this);
        this.viewer.destroy();
        store._destroyState(this.isbn);
      },
      getHighlights() {
        return store._getHighlights(this.isbn);
      },
      getHighlight(syncId) {
        return store._getHighlight(this.isbn, syncId);
      },
      getBookmarks() {
        return store._getBookmarks(this.isbn);
      },
      getBookmark(guid) {
        return store._getBookmark(this.isbn, guid);
      },
      getFigures() {
        return store._getFigures(this.isbn);
      },
      hasFeatureFlag(flag) {
        return store.hasFeatureFlag(this.isbn, flag);
      },
      getPageBreaks() {
        return store._getPageBreaks(this.isbn);
      },
      getTOC() {
        return store._getTOC(this.isbn);
      },
      getSpine() {
        return store._getSpine(this.isbn);
      },
      goTo(options) {
        return BookUtils.goTo(this.isbn, options);
      },
      clearSelection() {
        this.viewer.Book.clearSelection();
      },
      updateZoomScale() {
        return store._updateZoomScale(this.isbn);
      },
      setAutofocus(enabled) {
        // Do not need _updateState because we don't want a redraw
        this.settings.autofocus = enabled;
        this.viewer.Viewport.setAutofocus(enabled);
      }
    };
  },

  hasFeatureFlag(isbn, flag) {
    let book = this.get(isbn);
    if (book.features && book.features.length) {
      return book.features.some(f => f.name === flag);
    }
    return false;
  },

  _updateState(isbn, state) {
    // TODO
    this._state[isbn] = $.extend(this._state[isbn], state);
    Dispatcher.emit(Dispatcher.BOOK_CHANGE, isbn);
  },

  _destroyState(isbn) {
    //this._state[isbn] = undefined;
    this._state[isbn].isLoading = false;
    this._state[isbn].viewerIsReady = false;
    HighlightStore.cancel(isbn);
  },

  // TODO here is the original init function - might needs these arguments to call (such as redraw)
  //init: function(isbn, redraw, initialCFI, url, page){
  _createPageViewer(isbn, options) {
    let book = this.get(isbn);

    // TODO is this necessary?
    Fraction.Helpers.addLoader("#jigsaw-placeholder-outer");

    if (!book.isLoading && book.isbn) {
      book._updateState({ isLoading: true });
      book.viewer = Fraction.Viewer = Jigsaw.createPageViewer(
        {
          // the one exception to not using _updateState
          vbid: book.isbn,
          url: options.url,
          cfi: options.initialCFI,
          page: options.page || options.initialPage,
          highlightsEnabled: options.highlightsEnabled || true,
          swipeDisabled: options.swipeDisabled || true,
          brand: Fraction.Brand.jigsawBrand,
          start: options.start,
          end: options.end,
          autofocus: false,
          el: options.elementId || "jigsaw-placeholder",
          zoomDisabled: options.zoomDisabled || true,
          dpsOn: options.dpsOn,
          noteIcon: { svgIcon: true },
          imageButtons: options.imageButtons
        },
        response => {
          if (!options.pageOnly) {
            this._handlePageViewerResponse(isbn, response, options);
            BookAction.bookOpened(isbn);
          }
        }
      );
    }
  },

  // handle the page viewer response.
  // We need to include the isbn here because the error
  // responses do not pass that back to us.
  // TODO add this to jigsaw and everything will be better
  _handlePageViewerResponse(isbn, response, options) {
    let book = this.get(isbn);
    if (response.isSuccess()) {
      book = this.get(response.data.isbn);
      BookHelpers.bindToJigsawEvents(book);

      // TODO does this ever happen? Why is this happening?
      if (this.bookIsRebuilding) {
        // TODO do we need to do anything??
        book.viewer.bind("page:ready", function() {
          var oldRoute = Fraction.Router.getRoute().join("/");
          Fraction.Router.setRoute("/");
          Fraction.Router.setRoute(oldRoute);
        });
        return;
      }

      /*
        isbn, title, hasAncillaries, hasFigures, vbkType, layout, coverURL,
        author, edition, chapterTitle, printLimit, hasPageBreaks,
        bookIsRebuilding, bookDoesNotExist, bookAccessDenied, viewerIsReady,
        addedAt, apiPathPrefix, copyLimit, coverURL, expiresAt, format,
        lsiId, pathPrefix, url, viewedAt, settings
      */

      const newState = Object.assign({}, response.data, {
        title: response.data.title.decodeHTML(),
        chapterTitle: response.data.chapterTitle
          ? response.data.chapterTitle.decodeHTML()
          : "",
        bookIsRebuilding: false,
        bookDoesNotExist: false,
        bookAccessDenied: false,
        viewerIsReady: true
      });
      book._updateState(newState);

      book.setAutofocus(Fraction.Helpers.Users.getUserData("allow-cookies"));

      // Load pagebraks, toc, figures, highlights, etc
      this._loadBookData(book.isbn);

      // TODO loader removal should go elsewhere
      Fraction.Helpers.removeLoader("#jigsaw-placeholder-outer");

      // TODO what to do with this, probably doesn't belong here
      Fraction.Helpers.touchBook(book.isbn);

      // fire that the book loaded
    } else if (response.data.code === 423) {
      // Book is rebuilding
      book._updateState({ bookIsRebuilding: true });

      // TODO removeLoader probably doesn't belong here
      Fraction.Helpers.removeLoader("#book-container");
    } else if (response.data.code === 404) {
      if (response.data.jigsawCode === "bookNotFound") {
        // book does not exist
        book._updateState({ bookDoesNotExist: true });

        // TODO removeLoader probably doesn't belong here
        Fraction.Helpers.removeLoader("#book-container");
      } else if (response.data.jigsawCode === "pageNotFound") {
        // TODO this response definitely needs some refactoring
        // TODO there's a chance we don't return pageNotFound as an error anymore?
        // page does not exist
        var recentUrl = "/books/" + book.isbn + "/recent";

        if (!book.viewerIsReady) {
          // If a valid page hasn't loaded yet, try reinitializing with the last
          // viewed page (#/books/:isbn/recent)
          $("#jigsaw-placeholder")
            .find("iframe")
            .each(function() {
              this.src = "";
            });
          $("#jigsaw-placeholder").html("");
          book._updateState({ isLoading: false });
          this._createPageViewer(isbn, {
            page: "recent",
            dpsOn: options.dpsOn
          });
        } else {
          // redirect to the last viewed page
          // TODO what redirect fun is correct? this is new
          // window.location = "#" + recentUrl;
          // this is primary
          Fraction.Helpers.Locations.safeRedirect("#" + recentUrl);
        }

        // TODO not translated and doesn't belong here
        if (options && options.page) {
          Fraction.Helpers.toast("Page Not Found");
        }
      }
    } else if (response.data.code === 403) {
      if (response.data.jigsawCode === "bookNotActivated") {
        Fraction.Router.setRoute(`/activate/${isbn}`);
      } else {
        // No Access
        book._updateState({ bookAccessDenied: true });
        // TODO loader doesn't belong here
        Fraction.Helpers.removeLoader("#book-container");
      }
    } else if (response.data.code === 428) {
      Fraction.Helpers.removeLoader("#book-container");
    } else if (response.data.code === 401) {
      // Unauthorized
      Fraction.Helpers.signin();
    } else {
      Fraction.Helpers.signin();
    }
    book._updateState({ isLoading: false });
  },

  _load(isbn, options) {
    // TODO we don't want dupe page viewers for now, so this will eventually
    // TODO take over what is in book-helper.jsx
    // this._createPageViewer(isbn, options);
  },

  _adjacentPageEvent(book, key, response) {
    let newState = {};
    newState[key] = false;
    if (response && response.isSuccess()) {
      if (response.data.chapterTitle) {
        response.data.chapterTitle = response.data.chapterTitle.decodeHTML();
      }
      newState[key] = response.data;
    }
    book._updateState(newState);
  },

  _loadBookData(isbn) {
    //if (book.hasPagebreaks){ //TODO Check this
    this._loadPageBreaks(isbn);
    //}
    this._loadTOCData(isbn);
    this._loadSpine(isbn);
    HighlightStore._load(isbn);
    BookmarkStore._load(isbn);
    this._loadFigures(isbn); // TODO probably should be lazy
  },

  _updateZoomScale(isbn) {
    const book = this.get(isbn);
    if (book.viewer) {
      book.viewer.Book.getZoomScale(response => {
        if (response.isSuccess() && response.data.zoomScale) {
          this._updateState({ zoomScale: response.data.zoomScale });
        }
      });
    }
  },

  // TODO remove tmp functions
  __tmpSetTOC(isbn, data) {
    const book = this.get(isbn);
    book._updateState({ tocLoading: false, toc: data });
    Dispatcher.emit(Dispatcher.BOOK_TOC_LOADED, isbn);
  },
  __tmpSetFigures(isbn, data) {
    const book = this.get(isbn);
    book._updateState({ figuresLoading: false, figures: data });
  },
  __tmpSetPagebreaks(isbn, data) {
    const book = this.get(isbn);
    book._updateState({
      pagebreaksLoading: false,
      pagebreaks: data.pagebreaks,
      pageBreaksObject: data.pageBreaksObject
    });
  },

  // Loads the TOC data (TODO maybe this should be a get?)
  // This does not have it's own store because it will not change.
  _loadTOCData(isbn) {
    const book = this.get(isbn);
    if (book.viewer && book.viewerIsReady && !book.tocLoading) {
      book._updateState({ tocLoading: true });
      book.viewer.Book.getTOC(response => {
        if (response.isSuccess() && response.data) {
          book._updateState({ toc: response.data.toc, tocLoading: false });
        } else {
          book._updateState({ tocLoading: false });
        }
        Dispatcher.emit(Dispatcher.BOOK_TOC_LOADED, isbn);
      });
    }
  },

  // Loads the Spine data
  _loadSpine(isbn) {
    const book = this.get(isbn);
    if (book.viewer && book.viewerIsReady && !book.spineLoading) {
      book._updateState({ spineLoading: true });
      book.viewer.Book.getSpine(response => {
        if (response.isSuccess() && response.data) {
          book._updateState({
            spine: response.data.spine,
            spineLoading: false
          });
        } else {
          book._updateState({ spineLoading: false });
        }
        Dispatcher.emit(Dispatcher.BOOK_SPINE_LOADED, isbn);
      });
    }
  },

  // Loads the Figures data (TODO maybe this should be a get?)
  // This does not have it's own store because it will not change.
  _loadFigures(isbn) {
    let book = this.get(isbn);
    if (book.viewer && book.viewerIsReady && !book.figuresLoading) {
      book._updateState({ figuresLoading: true });
      book.viewer.Book.getFigures(response => {
        if (response.isSuccess() && response.data) {
          // TODO fix path in fraction to imageURL and you can remove the next two lines
          let figures = response.data.figures;
          // guess we're mapping to this path object.
          figures.map(figure => {
            figure.path = figure.imageURL;
            figure.figureId = Fraction.Helpers.generateGUID();
          });
          book._updateState({ figures: figures, figuresLoading: false });
        } else {
          book._updateState({ figuresLoading: false });
        }
        Dispatcher.emit(Dispatcher.BOOK_FIGURES_LOADED, book.isbn);
      });
    }
  },

  // Get just the list of figures for the given isbn
  _getFigures(isbn) {
    return this.get(isbn).figures;
  },

  // Get just the list of pagebreaks for the given isbn
  _getPageBreaks(isbn) {
    return this.get(isbn).pagebreaks;
  },

  // Get just the list of figures for the given isbn
  _getTOC(isbn) {
    return this.get(isbn).toc;
  },

  _getSpine(isbn) {
    return this.get(isbn).spine;
  },

  // get all the highlights of the book
  _getHighlights(isbn) {
    return HighlightStore.getAll(isbn);
  },

  // get specific highlight of book
  _getHighlight(isbn, syncId) {
    return HighlightStore.get(isbn, syncId);
  },

  // get all the bookmarks of the book
  _getBookmarks(isbn) {
    return BookmarkStore.get(isbn);
  },

  // get specific bookmark of book
  _getBookmark(isbn, guid) {
    return BookmarkStore.get(isbn, guid);
  },

  // search through highlights in the current book
  _searchHighlights(isbn, query, searchNotes, searchHighlights) {
    return HighlightStore.searchHighlights(
      isbn,
      query,
      searchNotes,
      searchHighlights
    );
  },

  // load all the pagebreaks of the book
  _loadPageBreaks(isbn) {
    let book = this.get(isbn);
    if (
      book.hasPageBreaks &&
      book.viewer &&
      !book.pagebreaksLoading &&
      book.viewerIsReady
    ) {
      book._updateState({ pagebreaksLoading: true });
      book.viewer.Book.getPageBreaks(response => {
        if (
          response.isSuccess() &&
          response.data &&
          response.data.pagelist &&
          response.data.pagelist.length > 0
        ) {
          book._updateState({
            pagebreaks: response.data.pagelist,
            pageBreaksObject: this._generatePageBreaksObject(
              response.data.pagelist
            ),
            pagebreaksLoading: false
          });
        } else {
          book._updateState({ pagebreaksLoading: false });
        }
        Dispatcher.emit(Dispatcher.BOOK_PAGEBREAKS_LOADED, isbn);
      });
    }
  },

  // Object to return a useful lookup table for finding
  // a page for cfi and a cfi for a page
  _generatePageBreaksObject(pagebreaks) {
    let pageBreaksObject = {};
    for (let i = 0; i < pagebreaks.length; i++) {
      let pg = pagebreaks[i].cfi.split("!")[0];
      let pageWithoutAssertions = Fraction.Helpers.stripAssertions(pg);
      if (!pageBreaksObject[pg]) {
        pageBreaksObject[pg] = [];
        pageBreaksObject[pageWithoutAssertions] = [];
      }
      pageBreaksObject[pg].push(pagebreaks[i]);
      pageBreaksObject[pageWithoutAssertions].push(pagebreaks[i]);
    }
    return pageBreaksObject;
  },

  _loadAdjacentPages(isbn) {
    const book = this.get(isbn);
    const nextPageLoader = () => {
      book.viewer.Book.getNextPage(response => {
        this._adjacentPageEvent(book, "nextPage", response);
      });
    };
    const prevPageLoader = () => {
      book.viewer.Book.getPreviousPage(response => {
        this._adjacentPageEvent(book, "previousPage", response);
      });
    };
    // reset state for snippets
    book.viewer.Snippet.isActive(active => {
      nextPageLoader();
      if (active.data) {
        book.viewer.Snippet.hasPreviousPage(resp => {
          if (resp.data) {
            prevPageLoader();
          } else {
            book._updateState({ previousPage: false });
          }
        });
      } else {
        prevPageLoader();
      }
    });
  },

  init() {
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 6);

    // on book:pagebreak
    Dispatcher.bind(Dispatcher.BOOK_PAGEBREAK, (isbn, e, data) => {
      const book = this.get(isbn);
      book._updateState({
        hasPages: book.hasPages || data.page,
        currentPage: data
      });
      // TODO probably doesn't belong here
      Fraction.Helpers.logNPSFeedbackEvent("page-views");
    });

    // on page:ready
    Dispatcher.bind(Dispatcher.BOOK_PAGE_READY, (isbn, e, data) => {
      const book = this.get(isbn);

      // TODO definitely doesn't belong here..
      if (data.code === 404) {
        if (data.jigsawCode === "highlightNotFound") {
          Fraction.Helpers.toast(i18n.t("highlighters.highlight-not-found"));
        }
        // Navigate back to last known location
        book.goTo({ cfi: `${book.pageCFI}!${book.lastKnownPageCFI}` });
      }

      if (data && data.cfi) {
        let newState = {
          pageCFI: data.cfi.split("!")[0],
          hasPages: this.hasPages || data.page,
          lastCFI: data.cfi || book.lastCFI
        };
        if (data.page) {
          newState.currentPage = data.page;
        }
        book._updateState(newState);

        this._loadAdjacentPages(isbn);
        this._updateZoomScale(isbn);
      }
    });

    Dispatcher.bind(Dispatcher.BOOK_SNIPPET_READY, isbn => {
      this._loadAdjacentPages(isbn);
    });

    Dispatcher.bind(Dispatcher.BOOK_SNIPPET_EXIT, isbn => {
      this._loadAdjacentPages(isbn);
    });

    // on page:load
    Dispatcher.bind(Dispatcher.BOOK_PAGE_LOAD, (isbn, _, data) => {
      const book = this.get(isbn);
      const newState = {};

      if (data && data.cfi) {
        newState.pageCFI = book.pageCFI || data.cfi.split("!")[0];
        newState.hasPages = book.hasPages || data.page;
        if (data.page) {
          newState.currentPage = data.page;
        }
      }

      book._updateState(newState);
    });

    Dispatcher.bind(Dispatcher.BOOK_NAVIGATION_REQUESTED, (isbn, data) => {
      // TODO probably doesn't belong here
      Fraction.Helpers.addLoader("#jigsaw-placeholder-outer");
    });

    Dispatcher.bind(Dispatcher.BOOK_PAGE_SCROLL, (isbn, e, data) => {
      const currentPage = BookUtils.getPageForCFI(isbn, data.cfi);
      const book = this.get(isbn);
      book._updateState({
        currentPage: currentPage,
        lastKnownPageCFI: data.cfi,
        lastCFI: data.cfi || book.lastCFI
      });
    });

    Dispatcher.bind(Dispatcher.USER_SIGNOUT, () => {
      Fraction.Helpers.signUserOut(); // We'll want some sort of messaging around this at some point.
    });
  }
};

BookStore.init();

export default BookStore;
